<template>
  <v-container fluid pa-0 v-if="$hasMatchingTenantRoles(['tenant_admin'])">
    <v-row no-gutters v-if="!loading">
      <v-col cols="12">
        <v-row no-gutters>
          <v-col cols="12" md="5" class="pt-md-5">
            <div class="image-holder">
              <div class="grey lighten-5 rounded-circle" style="width: 100%; height: 100%">
                <v-slide-x-transition>
                  <v-row v-if="imageIsLoaded" align="center" class="fill-height" justify="center" no-gutters>
                    <img src="img/undraw_connecting_teams_re_hno7.svg" style="height: 90%" />
                  </v-row>
                </v-slide-x-transition>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="7">
            <dismiss-onboarding-button />
            <v-fade-transition group hide-on-leave>
              <div class="text-left" v-if="step === 0" key="0">
                <div class="title text-sm-h5 font-weight-bold mt-5 mb-5">
                  {{ $t('onboarding.target_groups') }}
                </div>
                <div class="subtitle-2 text-sm-subtitle-1">{{ $t('onboarding.target_groups_info') }}</div>
                <div class="text-body-2 text-sm-body-1 primary--text text--darken-1 mt-4">
                  {{ $t('onboarding.target_groups_info2') }}
                </div>
                <basic-button @click="step = 1" color="primary" class="mt-10" outlined>
                  <v-icon left>mdi mdi-arrow-right</v-icon>
                  {{ $t('onboarding.continue') }}
                </basic-button>
              </div>

              <div v-if="step === 1" key="1">
                <div class="title text-sm-h5 font-weight-bold mt-5 mb-5">
                  {{ $t('onboarding.personnel_team') }}
                </div>
                <div class="subtitle-2 text-sm-subtitle-1">
                  {{ $t('onboarding.personnel_team_explained') }}
                </div>
                <div class="text-body-2 text-sm-body-1 primary--text text--darken-1 mt-4">
                  {{ $t('onboarding.you_can_create_one_or_more_teams') }}
                </div>
                <div v-if="personnelTeams.length > 0" class="mt-4 font-weight-bold">
                  {{ $t('onboarding.created_teams') }}:
                  <template v-for="(team, index) in personnelTeams">
                    <span :key="team.key" :style="{ color: $randomizeColorFromString(team.key) }">{{ team.name }}</span>
                    <span :key="team.key + 'divider'">{{ personnelTeams.length - 1 > index ? ', ' : '' }}</span>
                  </template>
                </div>
                <div class="mb-10 mt-10">
                  <basic-button @click="createTeam('PERSONNEL')" color="primary">
                    {{ $t('onboarding.create_personnel_team') }}
                  </basic-button>
                </div>

                <div>
                  <basic-button @click="step = 0" color="primary" outlined class="mr-2">
                    <v-icon left>mdi mdi-arrow-left</v-icon>
                    {{ $t('onboarding.back') }}
                  </basic-button>
                  <basic-button @click="step = 2" color="primary" outlined :disabled="personnelTeams.length === 0">
                    <v-icon left>mdi mdi-arrow-right</v-icon>
                    {{ $t('onboarding.continue') }}
                  </basic-button>
                </div>
              </div>

              <div v-if="step === 2" key="2">
                <div class="title text-sm-h5 font-weight-bold mt-5 mb-5">
                  {{ $t('onboarding.management_team') }}
                </div>
                <div class="subtitle-2 text-sm-subtitle-1">
                  {{ $t('onboarding.management_team_explained') }}
                </div>
                <div class="text-body-2 text-sm-body-1 primary--text text--darken-1 mt-4">
                  {{ $t('onboarding.you_can_create_one_or_more_teams') }}
                </div>
                <div v-if="managementTeams.length > 0" class="mt-4 font-weight-bold">
                  {{ $t('onboarding.created_teams') }}:
                  <template v-for="(team, index) in managementTeams">
                    <span :key="team.key" :style="{ color: $randomizeColorFromString(team.key) }">{{ team.name }}</span>
                    <span :key="team.key + 'divider'">{{ managementTeams.length - 1 > index ? ', ' : '' }}</span>
                  </template>
                </div>
                <div class="mb-10 mt-10">
                  <basic-button @click="createTeam('MANAGEMENT')" color="primary">
                    {{ $t('onboarding.create_management_team') }}
                  </basic-button>
                </div>

                <div>
                  <basic-button @click="step = 1" color="primary" outlined class="mr-2">
                    <v-icon left>mdi mdi-arrow-left</v-icon>
                    {{ $t('onboarding.back') }}
                  </basic-button>
                  <basic-button @click="goToNextStep" color="primary" outlined :disabled="managementTeams.length === 0">
                    <v-icon left>mdi mdi-arrow-right</v-icon>
                    {{ $t('onboarding.continue') }}
                  </basic-button>
                </div>
              </div>
            </v-fade-transition>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <edit-team-dialog ref="editTeamDialog" @refresh="reloadTeams" />

    <img src="img/undraw_connecting_teams_re_hno7.svg" style="display: none" @load="imageIsLoaded = true" />
  </v-container>
</template>

<script>
import EditTeamDialog from '@/components/Tenant/EditTeamDialog';
import BasicButton from '@/components/BasicButton';
import DismissOnboardingButton from '@/components/Tenant/Onboarding/DismissOnboardingButton';
export default {
  name: 'CreateTeams',
  components: { DismissOnboardingButton, BasicButton, EditTeamDialog },
  props: {
    teams: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      imageIsLoaded: false,
      loading: true,
      step: 0,
    };
  },
  methods: {
    goToNextStep() {
      this.$emit('next-step');
    },
    async reloadTeams() {
      this.$emit('refresh-teams');
    },
    createTeam(type) {
      this.$refs.editTeamDialog.open(null, type);
    },
  },
  computed: {
    personnelTeams() {
      return this.teams.filter(t => t.type === 'PERSONNEL');
    },
    managementTeams() {
      return this.teams.filter(t => t.type === 'MANAGEMENT');
    },
  },
  async mounted() {
    await this.reloadTeams();
    this.loading = false;
  },
};
</script>

<style lang="scss" scoped>
.image-holder {
  position: relative;
  margin: auto;
  width: 245px;
  height: 245px;
}

@media screen and (min-width: 1500px) {
  .image-holder {
    width: 285px;
    height: 285px;
  }
}
</style>
